import { useState, useEffect, useRef, Ref } from 'react';
import { defineOffsetLeft } from '../helpers/helpers';

type UseOffsetLeft = [number, Ref<HTMLTableCellElement>];

/**
 * This hook is responsible for manipulating the cell left position for frozen
 * TableHeaders and TableCells that can, for many reasons, be more than 0px away
 * from the parent left border. It's important so 'sticky' position can anchor
 * them to the correct scroll point.
 *
 * It uses the cellTagRef to send the element to the defineOffsetLeft helper and
 * get back the calculated distance it should be positioned when first loaded.
 *
 * If the browser also supports the `document.fonts` API, we'll recalculate this
 * distance just after the browser finishes loading the fonts, what changes
 * the content width, impacting on the overall cell position.
 *
 * @param freeze Boolean value defining if the element is frozen or not
 * @returns [offsetLeft, cellTagRef] Left distance as a number and the cell ref
 */

function useOffsetLeft(freeze?: boolean): UseOffsetLeft {
  const cellTagRef = useRef<HTMLTableCellElement>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const updateOffsetLeft = () => {
    const element = cellTagRef.current;
    if (element) {
      const newOffsetLeft = defineOffsetLeft(element);
      if (offsetLeft !== newOffsetLeft) setOffsetLeft(newOffsetLeft);
    }
  };

  useEffect(() => {
    if (freeze) {
      updateOffsetLeft();
      // @ts-ignore
      if (document.fonts) document.fonts.ready.then(updateOffsetLeft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellTagRef, freeze, offsetLeft]);

  return [offsetLeft, cellTagRef];
}

export default useOffsetLeft;
